import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import toast from 'react-hot-toast';
import { 
  FaEnvelope, FaPaperPlane, FaSearch, FaCheckCircle, FaTimesCircle, 
  FaUsers, FaFilePdf, FaChartLine, FaCalendar, 
  FaUserEdit, FaEye, FaRegClock, FaChevronLeft, FaChevronRight, FaSync
} from 'react-icons/fa';
import Loader from '../components/Loader';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];
const currentYear = new Date().getFullYear();
const currentMonth = months[new Date().getMonth()];
const years = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

const Email = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [receipts, setReceipts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [emailStats, setEmailStats] = useState({ delivered: 0, opened: 0, clicked: 0 });
  
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchReceipts();
    fetchEmailStats();
  }, []);

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/receipts?limit=1000');
      setReceipts(response.data.receipts || []);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load receipts', {
        icon: '❌',
        style: { borderRadius: '10px', background: '#EF4444', color: '#fff' },
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchEmailStats = async () => {
    try {
      const response = await api.get('/email/stats');
      setEmailStats(response.data);
    } catch (error) {
      // ignore
    }
  };

  // ---- Filter ----
  const getFilteredReceipts = () => {
    let filtered = receipts;
    if (selectedMonth && selectedYear) {
      filtered = filtered.filter(r => r.month === selectedMonth && r.year === selectedYear);
    }
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(r =>
        r.tenantName.toLowerCase().includes(term) ||
        r.receiptNumber.toLowerCase().includes(term) ||
        (r.tenantEmail && r.tenantEmail.toLowerCase().includes(term)) ||
        r.tenantPhone.includes(term)
      );
    }
    return filtered;
  };

  const filteredAll = getFilteredReceipts();
  const totalPages = Math.ceil(filteredAll.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedReceipts = filteredAll.slice(startIndex, startIndex + itemsPerPage);

  // ---- Send individual email ----
  const handleSendEmail = async (receiptId, email = null) => {
    setSending(true);
    try {
      const receipt = receipts.find(r => r._id === receiptId);
      const recipientEmail = email || receipt.tenantEmail;
      if (!recipientEmail) {
        toast.error('No email address available');
        setSending(false);
        return;
      }

      // Default template (hardcoded, no user editing)
      const subject = `Payment Receipt ${receipt.receiptNumber} - ${user.pgName}`;
      const message = 
        `Dear ${receipt.tenantName},\n\n` +
        `Please find attached your payment receipt for ${receipt.forMonth} (₹${receipt.amountPaid?.toLocaleString('en-IN')}).\n\n` +
        `Thank you for your payment.\n\n` +
        `Regards,\n${user.pgName || 'PG Management'}`;

      await api.post('/email/send', {
        receiptId,
        recipientEmail,
        subject,
        message
      });
      
      toast.success(`Email sent to ${receipt.tenantName}!`, {
        icon: '✉️',
        style: { borderRadius: '10px', background: '#10B981', color: '#fff' },
      });
      
      setReceipts(prev => prev.map(r =>
        r._id === receiptId ? { ...r, sentViaEmail: true, emailSentAt: new Date() } : r
      ));
    } catch (error) {
      toast.error('Failed to send email', {
        icon: '❌',
        style: { borderRadius: '10px', background: '#EF4444', color: '#fff' },
      });
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Stats
  const total = receipts.length;
  const sent = receipts.filter(r => r.sentViaEmail).length;
  const pending = receipts.filter(r => !r.sentViaEmail && r.tenantEmail).length;
  const noEmail = receipts.filter(r => !r.tenantEmail).length;
  const successRate = total > 0 ? (sent / total) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8 transition-colors duration-300">
      
      {/* Header - without Settings/Alerts */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-md">
                <FaEnvelope className="h-6 w-6 text-white" />
              </div>
              <h1 className="text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
                Email Manager
              </h1>
            </div>
            <p className="text-gray-600 dark:text-gray-400 text-sm md:text-base">
              Send individual receipt emails to tenants
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
        {[
          { title: 'Total Receipts', value: total, icon: FaFilePdf, color: 'from-blue-500 to-blue-600', desc: 'All time' },
          { title: 'Emails Sent', value: sent, icon: FaPaperPlane, color: 'from-emerald-500 to-green-500', desc: `${successRate.toFixed(1)}% rate` },
          { title: 'Pending', value: pending, icon: FaRegClock, color: 'from-amber-500 to-orange-500', desc: 'Need attention' },
          { title: 'No Email', value: noEmail, icon: FaUsers, color: 'from-rose-500 to-pink-500', desc: 'Add email to send' }
        ].map((stat, idx) => (
          <div key={idx} className={`bg-gradient-to-br ${stat.color} text-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 p-5 md:p-6`}>
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-sm opacity-90 mb-1">{stat.title}</p>
                <p className="text-2xl md:text-3xl font-bold">{stat.value}</p>
              </div>
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                <stat.icon className="h-5 w-5 md:h-6 md:w-6" />
              </div>
            </div>
            <div className="pt-4 border-t border-white/20">
              <span className="text-sm opacity-90">{stat.desc}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Search & Filters */}
      <div className="mb-6 md:mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-5 md:p-6">
            <div className="flex flex-col md:flex-row gap-4 items-start md:items-center">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400 dark:text-gray-500" />
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-12 pr-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Search by tenant name, email, receipt number..."
                />
              </div>
              
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="block w-40 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
                >
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  className="block w-28 px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:focus:ring-blue-800 transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white appearance-none"
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
                <button
                  onClick={fetchReceipts}
                  className="flex items-center gap-2 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all"
                  title="Refresh data"
                >
                  <FaSync className="h-4 w-4" />
                  <span className="text-sm font-medium hidden sm:inline">Refresh</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Receipts Table - no checkboxes, no bulk actions */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700 mb-6 md:mb-8">
        <div className="p-5 md:p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white">Receipts List</h3>
              <p className="text-gray-600 dark:text-gray-400 text-sm mt-1">
                Showing {paginatedReceipts.length} of {filteredAll.length} receipts
                {selectedMonth && selectedYear && ` for ${selectedMonth} ${selectedYear}`}
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="font-medium text-green-600 dark:text-green-400">{sent}</span> sent •
              <span className="font-medium text-amber-600 dark:text-amber-400">{pending}</span> pending •
              <span className="font-medium text-rose-600 dark:text-rose-400">{noEmail}</span> no email
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-12 flex items-center justify-center">
            <Loader size="large" />
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading receipts...</p>
          </div>
        ) : paginatedReceipts.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-600 rounded-full flex items-center justify-center">
              <FaEnvelope className="h-12 w-12 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
              {searchTerm ? 'No matching receipts' : 'No receipts for this period'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-6">
              {searchTerm 
                ? 'Try adjusting your search or change the month/year'
                : 'Generate receipts to start sending emails'
              }
            </p>
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedMonth(currentMonth);
                setSelectedYear(currentYear);
              }}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all shadow-md"
            >
              {searchTerm ? 'Clear Search' : 'Go to Current Month'}
            </button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700">
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Receipt</th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Tenant</th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Payment</th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Status</th>
                    <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300 text-sm">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedReceipts.map((receipt) => (
                    <tr key={receipt._id} className="border-b border-gray-100 dark:border-gray-700 hover:bg-blue-50/50 dark:hover:bg-blue-900/20 transition-colors duration-150">
                      <td className="p-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30 flex items-center justify-center mr-3">
                            <FaFilePdf className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <p className="font-bold text-blue-600 dark:text-blue-400 text-sm">{receipt.receiptNumber}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              <FaCalendar className="inline mr-1" />
                              {formatDate(receipt.createdAt)}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{receipt.tenantName}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{receipt.tenantPhone}</p>
                        {receipt.tenantEmail ? (
                          <p className="text-sm text-blue-600 dark:text-blue-400 truncate max-w-xs">{receipt.tenantEmail}</p>
                        ) : (
                          <p className="text-sm text-rose-600 dark:text-rose-400">No email</p>
                        )}
                      </td>
                      <td className="p-4">
                        <p className="font-bold text-green-600 dark:text-green-400 text-sm">
                          ₹{receipt.amountPaid?.toLocaleString('en-IN')}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded inline-block mt-1">
                          {receipt.forMonth}
                        </p>
                      </td>
                      <td className="p-4">
                        {receipt.sentViaEmail ? (
                          <div className="flex items-center">
                            <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
                              <FaCheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <span className="text-green-700 dark:text-green-400 font-medium text-sm">Sent</span>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {receipt.emailSentAt ? formatDate(receipt.emailSentAt) : ''}
                              </p>
                            </div>
                          </div>
                        ) : receipt.tenantEmail ? (
                          <div className="flex items-center">
                            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg mr-3">
                              <FaRegClock className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                            </div>
                            <span className="text-amber-700 dark:text-amber-400 font-medium text-sm">Pending</span>
                          </div>
                        ) : (
                          <div className="flex items-center">
                            <div className="p-2 bg-rose-100 dark:bg-rose-900/30 rounded-lg mr-3">
                              <FaTimesCircle className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                            </div>
                            <span className="text-rose-700 dark:text-rose-400 font-medium text-sm">No Email</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2 opacity-70 hover:opacity-100 transition-opacity duration-200">
                          {receipt.tenantEmail && (
                            <button
                              onClick={() => handleSendEmail(receipt._id)}
                              disabled={sending || receipt.sentViaEmail}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                                receipt.sentViaEmail
                                  ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                              }`}
                              title={receipt.sentViaEmail ? 'Already sent' : 'Send receipt to tenant'}
                            >
                              <FaPaperPlane className="h-3 w-3" />
                              {receipt.sentViaEmail ? 'Sent' : 'Send'}
                            </button>
                          )}
                          {!receipt.tenantEmail && (
                            <button
                              onClick={() => {
                                const email = prompt('Enter email address for tenant:');
                                if (email && email.includes('@')) {
                                  handleSendEmail(receipt._id, email);
                                } else if (email) {
                                  toast.error('Please enter a valid email address');
                                }
                              }}
                              disabled={sending}
                              className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all text-xs font-medium flex items-center gap-1"
                              title="Add email address and send receipt"
                            >
                              <FaUserEdit className="h-3 w-3" />
                              Add & Send
                            </button>
                          )}
                          <button
                            className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all text-xs font-medium flex items-center gap-1"
                            title="View receipt details"
                          >
                            <FaEye className="h-3 w-3" />
                            View
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="p-5 md:p-6 border-t border-gray-100 dark:border-gray-700">
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="text-sm text-gray-700 dark:text-gray-300">
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FaChevronLeft className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    </button>
                    <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-lg text-sm">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    >
                      <FaChevronRight className="h-4 w-4 text-gray-700 dark:text-gray-300" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Insights & Best Practices (kept for informational value) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-5 md:p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center">
              <FaChartLine className="mr-2 text-blue-600 dark:text-blue-400" />
              Email Performance
            </h3>
          </div>
          <div className="p-5 md:p-6">
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Delivery Rate</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{successRate.toFixed(1)}%</p>
                </div>
                <div className="w-24 h-24 relative">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-lg font-bold text-blue-600 dark:text-blue-400">{successRate.toFixed(0)}%</span>
                  </div>
                  <svg className="w-full h-full" viewBox="0 0 36 36">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#E5E7EB"
                      strokeWidth="3"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#3B82F6"
                      strokeWidth="3"
                      strokeDasharray={`${successRate}, 100`}
                    />
                  </svg>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Opened Rate</p>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{emailStats.opened}%</p>
                </div>
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">Clicked Rate</p>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{emailStats.clicked}%</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700">
          <div className="p-5 md:p-6 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-gray-50 to-white dark:from-gray-800 dark:to-gray-800">
            <h3 className="text-lg md:text-xl font-bold text-gray-900 dark:text-white flex items-center">
              <FaUsers className="mr-2 text-purple-600 dark:text-purple-400" />
              Quick Tips
            </h3>
          </div>
          <div className="p-5 md:p-6">
            <ul className="space-y-4">
              {[
                'Send receipts immediately after payment',
                'Ensure tenant email addresses are correct',
                'Use clear subject lines with receipt number',
                'Follow up if email is not opened within 24 hours',
                'Keep a record of all sent emails'
              ].map((tip, idx) => (
                <li key={idx} className="flex items-start">
                  <div className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30 mr-3 mt-0.5">
                    <FaCheckCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  </div>
                  <span className="text-sm text-gray-700 dark:text-gray-300">{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center text-gray-500 dark:text-gray-400 text-sm">
          <p>Email manager last updated • {new Date().toLocaleString('en-IN', { hour: '2-digit', minute: '2-digit' })}</p>
          <p className="mt-1">
            Need help? 
            <a href="/help" className="text-blue-600 dark:text-blue-400 hover:underline ml-1">Contact support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Email;